import React, { useEffect, useState } from 'react';
import api from '../api';
import "./estilodash.css";

const ListarLicencas = () => {
  const [clientes, setClientes] = useState([]);
  const [licencas, setLicencas] = useState([]);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('ativas');     // todos | ativas | inativas
  const [filtroTipo, setFiltroTipo] = useState('todos');          // todos | real | demo
  const [filtroExpiracao, setFiltroExpiracao] = useState('todos'); // todos | expiradas | nao-expiradas
  const [novaDataExpMap, setNovaDataExpMap] = useState({});
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 10;

  const [mensagem, setMensagem] = useState('');
  const [tipoMensagem, setTipoMensagem] = useState(''); // 'sucesso' | 'erro' | ''

  useEffect(() => {
    carregarTudo();
  }, []);

  const mostrarMensagem = (texto, tipo = 'sucesso') => {
    setMensagem(texto);
    setTipoMensagem(tipo);
    // se quiser limpar sozinho depois de alguns segundos:
    setTimeout(() => {
      setMensagem('');
      setTipoMensagem('');
    }, 5000);
  };

  const carregarTudo = async () => {
    try {
      setCarregando(true);
      setErro(null);

      const [respClientes, respLicencas] = await Promise.all([
        api.get('/api/clientes'),
        api.get('/api/licencas'),
      ]);

      const clientesData = respClientes.data || [];
      const licencasData = (respLicencas.data || []).map((lic) => ({
        ...lic,
        status: Number(lic.status),
      }));

      setClientes(clientesData);
      setLicencas(licencasData);
    } catch (error) {
      console.error('Erro ao carregar licenças/clientes:', error);
      setErro('Erro ao carregar dados. Verifique o servidor/login.');
      mostrarMensagem('Erro ao carregar dados de licenças/clientes.', 'erro');
    } finally {
      setCarregando(false);
    }
  };

  const getCliente = (id) => {
    return clientes.find((c) => Number(c.id) === Number(id)) || null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const isExpirada = (dateString) => {
    if (!dateString) return false;
    const hoje = new Date();
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return false;
    // Considera expirada se a data de expiração for anterior a hoje
    return d < hoje;
  };

  const handleAlterarTipo = async (licencaId, novoTipo) => {
    try {
      await api.put(`/api/licencas/${licencaId}/tipo`, { tipo: novoTipo });
      await carregarTudo();
      mostrarMensagem('Tipo da licença alterado com sucesso.', 'sucesso');
    } catch (error) {
      console.error('Erro ao alterar tipo:', error);
      mostrarMensagem('Erro ao alterar o tipo da licença.', 'erro');
    }
  };

  const handleAlterarDataExp = async (licencaId) => {
    const novaData = novaDataExpMap[licencaId];
    if (!novaData) {
      mostrarMensagem('Informe uma data de expiração válida antes de salvar.', 'erro');
      return;
    }

    try {
      await api.put(`/api/licencas/${licencaId}/data`, { date_exp: novaData });
      setNovaDataExpMap((prev) => ({ ...prev, [licencaId]: '' }));
      await carregarTudo();
      mostrarMensagem('Data de expiração alterada com sucesso.', 'sucesso');
    } catch (error) {
      console.error('Erro ao alterar data de expiração:', error);
      mostrarMensagem('Erro ao alterar a data de expiração da licença.', 'erro');
    }
  };

  const handleAlterarStatus = async (licenca) => {
    const novoStatus = licenca.status === 1 ? 0 : 1;
    try {
      await api.put(`/api/licencas/${licenca.id}/status`, { status: novoStatus });
      await carregarTudo();
      mostrarMensagem('Status da licença alterado com sucesso.', 'sucesso');
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      mostrarMensagem('Erro ao alterar o status da licença.', 'erro');
    }
  };

  const handleRemoverLicenca = async (id) => {
    const confirmar = window.confirm(
      'ATENÇÃO: Tem certeza que deseja remover definitivamente esta licença?'
    );
    if (!confirmar) return;

    try {
      await api.delete(`/api/licencas/${id}`);
      setLicencas((prev) => prev.filter((l) => l.id !== id));
      mostrarMensagem('Licença removida com sucesso.', 'sucesso');
    } catch (error) {
      console.error('Erro ao remover licença:', error);
      mostrarMensagem('Erro ao remover licença.', 'erro');
    }
  };

  // ================== FILTROS ==================

  let licencasFiltradas = licencas;

  // Filtro por texto: ID, cliente, conta, chave
  licencasFiltradas = licencasFiltradas.filter((lic) => {
    if (!busca) return true;
    const termo = busca.toLowerCase();

    const cliente = getCliente(lic.id_cliente);
    const nomeCliente = cliente?.nome?.toLowerCase() || '';
    const idCliente = cliente ? String(cliente.id) : '';

    return (
      String(lic.id).includes(termo) ||
      (lic.conta && String(lic.conta).toLowerCase().includes(termo)) ||
      (lic.lkey && String(lic.lkey).toLowerCase().includes(termo)) ||
      nomeCliente.includes(termo) ||
      idCliente.includes(termo)
    );
  });

  // Filtro por status
  if (filtroStatus === 'ativas') {
    licencasFiltradas = licencasFiltradas.filter((lic) => lic.status === 1);
  } else if (filtroStatus === 'inativas') {
    licencasFiltradas = licencasFiltradas.filter((lic) => lic.status !== 1);
  }

  // Filtro por tipo (Real/Demo)
  if (filtroTipo === 'real') {
    licencasFiltradas = licencasFiltradas.filter(
      (lic) => String(lic.tipo || '').toLowerCase() === 'real'
    );
  } else if (filtroTipo === 'demo') {
    licencasFiltradas = licencasFiltradas.filter(
      (lic) => String(lic.tipo || '').toLowerCase() === 'demo'
    );
  }

  // Filtro por expiração (novo)
  if (filtroExpiracao === 'expiradas') {
    licencasFiltradas = licencasFiltradas.filter((lic) =>
      isExpirada(lic.date_exp)
    );
  } else if (filtroExpiracao === 'nao-expiradas') {
    licencasFiltradas = licencasFiltradas.filter(
      (lic) => !isExpirada(lic.date_exp)
    );
  }

  // ================== PAGINAÇÃO ==================
  const totalPaginas = Math.ceil(licencasFiltradas.length / itensPorPagina) || 1;
  const paginaNormalizada = Math.min(paginaAtual, totalPaginas);
  const inicio = (paginaNormalizada - 1) * itensPorPagina;
  const fim = inicio + itensPorPagina;
  const licencasPaginadas = licencasFiltradas.slice(inicio, fim);

  return (
    <div className="clientes-container">
      <h3>Lista de Licenças</h3>

      {/* Mensagem de feedback */}
      {mensagem && (
        <p
          style={{
            color: tipoMensagem === 'erro' ? 'red' : 'green',
            marginBottom: '8px',
          }}
        >
          {mensagem}
        </p>
      )}

      {/* Filtros */}
      <div className="clientes-filtros">
        <label>
          Buscar:&nbsp;
          <input
            type="text"
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
              setPaginaAtual(1);
            }}
            placeholder="ID, cliente, conta, chave..."
            style={{ width: '40ch' }}
          />
        </label>

        <label>
          Status:&nbsp;
          <select
            value={filtroStatus}
            onChange={(e) => {
              setFiltroStatus(e.target.value);
              setPaginaAtual(1);
            }}
          >
            <option value="todos">Todos</option>
            <option value="ativas">Ativas</option>
            <option value="inativas">Inativas</option>
          </select>
        </label>

        <label>
          Tipo:&nbsp;
          <select
            value={filtroTipo}
            onChange={(e) => {
              setFiltroTipo(e.target.value);
              setPaginaAtual(1);
            }}
          >
            <option value="todos">Todos</option>
            <option value="real">Real</option>
            <option value="demo">Demo</option>
          </select>
        </label>

        <label>
          Expiração:&nbsp;
          <select
            value={filtroExpiracao}
            onChange={(e) => {
              setFiltroExpiracao(e.target.value);
              setPaginaAtual(1);
            }}
          >
            <option value="todos">Todas</option>
            <option value="expiradas">Expiradas</option>
            <option value="nao-expiradas">Não expiradas</option>
          </select>
        </label>

        <button onClick={carregarTudo}>Atualizar</button>
      </div>

      {carregando && <p>Carregando dados...</p>}
      {erro && <p style={{ color: 'red' }}>{erro}</p>}

      {/* Tabela de licenças */}
      <table className="clientes-tabela">
        <thead>
          <tr>
            <th>ID</th>
            <th>Cliente</th>
            <th>Conta</th>
            <th>Chave (lkey)</th>
            <th>Tipo</th>
            <th>Status</th>
            <th>Criação</th>
            <th>Expiração</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {licencasPaginadas.length === 0 && (
            <tr>
              <td colSpan="9" style={{ textAlign: 'center' }}>
                Nenhuma licença encontrada.
              </td>
            </tr>
          )}

          {licencasPaginadas.map((lic) => {
            const cliente = getCliente(lic.id_cliente);
            const expirada = isExpirada(lic.date_exp);

            return (
              <tr key={lic.id}>
                <td>{lic.id}</td>
                <td>
                  {cliente
                    ? `${cliente.id} - ${cliente.nome}`
                    : `ID Cliente: ${lic.id_cliente}`}
                </td>
                <td>{lic.conta}</td>
                <td>{lic.lkey}</td>
                <td>{lic.tipo}</td>
                <td>
                  <span
                    className={
                      lic.status === 1 ? 'status-ativa' : 'status-inativa'
                    }
                  >
                    {lic.status === 1 ? 'Ativa' : 'Inativa'}
                  </span>
                </td>
                <td>{formatDate(lic.created_on)}</td>
                <td>
                  <span style={{ color: expirada ? 'red' : 'inherit' }}>
                    {formatDate(lic.date_exp)}
                  </span>
                  <div style={{ marginTop: 4 }}>
                    <input
                      type="date"
                      style={{
                        fontSize: '11px',
                        width: '120px',
                        marginRight: 4,
                      }}
                      value={novaDataExpMap[lic.id] || ''}
                      onChange={(e) =>
                        setNovaDataExpMap((prev) => ({
                          ...prev,
                          [lic.id]: e.target.value,
                        }))
                      }
                    />
                    <button
                      style={{ padding: '2px 6px', fontSize: '11px' }}
                      onClick={() => handleAlterarDataExp(lic.id)}
                    >
                      Salvar
                    </button>
                  </div>
                </td>
                <td>
                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                  >
                    <select
                      value={lic.tipo || ''}
                      onChange={(e) =>
                        handleAlterarTipo(lic.id, e.target.value)
                      }
                      style={{
                        fontSize: '11px',
                        padding: '2px',
                        width: '80px',
                      }}
                    >
                      <option value="Real">Real</option>
                      <option value="Demo">Demo</option>
                    </select>

                    <button
                      style={{ padding: '3px 6px', fontSize: '11px' }}
                      onClick={() => handleAlterarStatus(lic)}
                    >
                      Status
                    </button>

                    <button
                      style={{
                        padding: '3px 6px',
                        fontSize: '11px',
                        color: '#fff',
                        backgroundColor: '#cf3025',
                      }}
                      onClick={() => handleRemoverLicenca(lic.id)}
                    >
                      Remover
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Paginação */}
      {totalPaginas > 1 && (
        <div style={{ textAlign: 'center', marginTop: '15px' }}>
          {Array.from({ length: totalPaginas }, (_, index) => (
            <button
              key={index}
              style={{
                margin: '0 4px',
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor:
                  paginaNormalizada === index + 1 ? '#1976d2' : '#e0e0e0',
                color: paginaNormalizada === index + 1 ? '#fff' : '#000',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
              onClick={() => setPaginaAtual(index + 1)}
            >
              {index + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ListarLicencas;
